import api from './api';

// Les fichiers /uploads/... exigent le jeton Bearer : <img src> et <a href> ne peuvent pas l'envoyer.
// On les charge via l'instance axios (jeton + gestion du 401) puis on les expose en blob: URL.

const MAX_CACHED = 100;
const cache = new Map(); // url -> blob: URL
const pending = new Map(); // url -> Promise<blob: URL> (evite les doubles chargements)

// Chemin /uploads/... same-origin, ou null. Le jeton n'est JAMAIS envoye vers une autre origine
// (une URL de fichier enregistree en base peut etre arbitraire).
function toUploadPath(url) {
  if (typeof url !== 'string' || !url) return null;
  let path = url;
  if (/^[a-z][a-z0-9+.-]*:/i.test(url) || url.startsWith('//')) {
    try {
      const u = new URL(url, window.location.origin);
      if (u.origin !== window.location.origin) return null;
      path = u.pathname;
    } catch (e) {
      return null;
    }
  }
  if (!path.startsWith('/uploads/') || path.includes('\\') || /(^|\/)\.\.?(\/|$)/.test(path)) return null;
  return path;
}

/**
 * Charge un fichier protege et renvoie une blob: URL (mise en cache).
 * Rejette avec une Error (propriete status = code HTTP si disponible).
 */
export function fetchFileBlobUrl(url) {
  const path = toUploadPath(url);
  if (!path) return Promise.reject(new Error('URL de fichier non autorisée'));
  if (cache.has(path)) return Promise.resolve(cache.get(path));
  if (pending.has(path)) return pending.get(path);

  const request = api
    .get(path, { baseURL: '', responseType: 'blob' })
    .then((res) => {
      const blobUrl = URL.createObjectURL(res.data);
      cache.set(path, blobUrl);
      if (cache.size > MAX_CACHED) releaseFileBlobUrl(cache.keys().next().value);
      return blobUrl;
    })
    .catch((err) => {
      const e = new Error('Fichier indisponible');
      e.status = err && err.response && err.response.status;
      throw e;
    })
    .finally(() => pending.delete(path));
  pending.set(path, request);
  return request;
}

/** Libere la blob: URL d'un fichier (ou toutes, sans argument). */
export function releaseFileBlobUrl(url) {
  if (url === undefined) {
    cache.forEach((blobUrl) => URL.revokeObjectURL(blobUrl));
    cache.clear();
    return;
  }
  const path = toUploadPath(url) || url;
  const blobUrl = cache.get(path);
  if (blobUrl) {
    URL.revokeObjectURL(blobUrl);
    cache.delete(path);
  }
}

/**
 * Ouvre un fichier protege dans un nouvel onglet, ou le telecharge ({ download: true, filename }).
 * Une URL externe http(s) (ancienne donnee) est simplement ouverte, sans jeton.
 */
export async function openAuthFile(url, { download = false, filename } = {}) {
  if (!toUploadPath(url)) {
    if (typeof url === 'string' && /^https?:\/\//i.test(url)) {
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }
    throw new Error('URL de fichier non autorisée');
  }

  // Onglet ouvert tout de suite (geste utilisateur) pour ne pas etre bloque par le navigateur.
  const win = download ? null : window.open('', '_blank');
  if (win) win.opener = null;
  try {
    const blobUrl = await fetchFileBlobUrl(url);
    if (download) {
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename || 'fichier';
      document.body.appendChild(link);
      link.click();
      link.remove();
    } else if (win) {
      win.location.href = blobUrl;
    } else {
      window.open(blobUrl, '_blank');
    }
  } catch (err) {
    if (win) win.close();
    throw err;
  }
}
